<template>
  <div class="chart-wrapper">
    <div class="chart-area">
      <div
        v-for="(item, index) in chartData"
        :key="index"
        class="chart-bar"
        :class="colorTheme"
        :style="{ height: `${(item.value / maxValue) * 100}%` }"
        @mouseenter="hoverIndex = index"
        @mouseleave="hoverIndex = null"
      >
        <div v-if="hoverIndex === index" class="tooltip">
          {{ formatTooltipValue(item.value) }}
        </div>
      </div>
    </div>
    <div class="chart-labels">
      <span v-for="(item, index) in chartData" :key="'lbl-' + index">
        {{ item.label }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

type UsageTabKey = "internet" | "minutes" | "sms";

const props = defineProps<{
  period: "week" | "month" | "year";
  activeTab: UsageTabKey;
  operations: Array<{
    date: string; // "24 октября"
    operations: Array<{
      amount: number;
      type: string;
      traffic: number;
      duration: number;
    }>;
  }>;
}>();

const store = useStore();
const { tm } = useI18n();
const colorTheme = ref<"moiOperator" | "psb" | "atom">("moiOperator");
const hoverIndex = ref<number | null>(null);

const monthNames = tm("format.monthsGenitive") as string[];

function convertToISO(dateHuman: string): string {
  // "24 октября" → "YYYY-10-24"
  const [day, monthName] = dateHuman.split(" ");
  const month = monthNames.indexOf(monthName) + 1;
  const y = new Date().getFullYear();
  return `${y}-${String(month).padStart(2, "0")}-${String(day).padStart(
    2,
    "0"
  )}`;
}

function getOperationMetricValue(op: {
  traffic: number;
  duration: number;
}): number {
  if (props.activeTab === "internet") return op.traffic || 0;
  if (props.activeTab === "minutes")
    return op.duration ? Math.ceil(op.duration / 60) : 0;
  return 1; // sms
}

function formatTooltipValue(sum: number): string {
  if (props.activeTab === "internet") {
    if (sum >= 1024 * 1024) return `${(sum / 1024 / 1024).toFixed(2)} ГБ`;
    if (sum >= 1024) return `${(sum / 1024).toFixed(1)} МБ`;
    return `${Math.round(sum)} КБ`;
  }
  if (props.activeTab === "minutes") return `${sum} мин`;
  return `${sum} SMS`;
}

// Flat list of { dateISO, metricValue }
const flat = computed(() =>
  props.operations.flatMap((day) => {
    const iso = convertToISO(day.date);
    return day.operations.map((op) => ({
      dateISO: iso,
      metricValue: getOperationMetricValue(op),
    }));
  })
);

const chartData = computed(() => {
  const result: Record<string, number> = {};
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const today = now.getDate();

  if (props.period === "week") {
    for (let i = 0; i < 7; i++) {
      const d = new Date(y, m, today - 6 + i);
      const key = `${d.getMonth() + 1}-${d.getDate()}`;
      result[key] = 0;
    }
  } else if (props.period === "month") {
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    for (let i = 1; i <= daysInMonth; i++) {
      result[i] = 0;
    }
  } else {
    for (let i = 1; i <= 12; i++) {
      result[i] = 0;
    }
  }

  // Sum metric values per slot
  flat.value.forEach((op) => {
    const d = new Date(op.dateISO);
    if (props.period === "week") {
      const key = `${d.getMonth() + 1}-${d.getDate()}`;
      if (result[key] !== undefined) result[key] += op.metricValue;
    } else if (props.period === "month") {
      const day = d.getDate();
      if (result[day] !== undefined) result[day] += op.metricValue;
    } else {
      const month = d.getMonth() + 1;
      if (result[month] !== undefined) result[month] += op.metricValue;
    }
  });

  if (props.period === "week") {
    const keys = Object.keys(result).sort((a, b) => {
      const [ma, da] = a.split("-").map(Number);
      const [mb, db] = b.split("-").map(Number);
      return (
        new Date(y, ma - 1, da).getTime() - new Date(y, mb - 1, db).getTime()
      );
    });
    return keys.map((key) => ({
      label: key.split("-")[1],
      value: result[key],
    }));
  } else {
    return Object.keys(result)
      .sort((a, b) => Number(a) - Number(b))
      .map((label) => ({ label, value: result[label] }));
  }
});

const maxValue = computed(() =>
  Math.max(...chartData.value.map((x) => x.value), 1)
);

onMounted(() => {
  colorTheme.value = store.state.brand.operatorSource;
});
</script>

<style scoped lang="scss">
@import "@/assets/style/colors.scss";

.chart-wrapper {
  width: 100%;
  padding: 12px 0;
}

.chart-area {
  height: 160px;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  position: relative;
}

.chart-bar {
  flex: 1;
  border-radius: 4px;
  position: relative;
  transition: 0.2s;
  min-height: 2px;

  &.moiOperator {
    background-color: $mo-secondary;

    &:hover {
      background-color: $mo-tertiary;
    }
  }

  &.psb {
    background-color: $psb-secondary;

    &:hover {
      background-color: $psb-tertiary;
    }
  }

  &.atom {
    background-color: $atom-secondary;

    &:hover {
      background-color: #004aa8ff;
    }
  }
}

.tooltip {
  position: absolute;
  top: -28px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  color: black;
  padding: 3px 6px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: bold;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.chart-labels {
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #4d4e78;
}
</style>
